import React, { useState } from 'react';

const INITIAL_BUDGET = [
  { id: 1, category: 'Sales Revenue', type: 'Income', budgeted: 100000, actual: 95000 },
  { id: 2, category: 'Service Revenue', type: 'Income', budgeted: 15000, actual: 12000 },
  { id: 3, category: 'Cost of Goods Sold', type: 'Expense', budgeted: 50000, actual: 47000 },
  { id: 4, category: 'Salaries Expense', type: 'Expense', budgeted: 20000, actual: 18000 },
  { id: 5, category: 'Rent Expense', type: 'Expense', budgeted: 8000, actual: 6000 },
  { id: 6, category: 'Marketing', type: 'Expense', budgeted: 5000, actual: 6200 },
  { id: 7, category: 'Utilities', type: 'Expense', budgeted: 2000, actual: 1800 },
];

const getProgressColor = (pct) => {
  if (pct <= 75) return 'green';
  if (pct <= 95) return 'yellow';
  return 'red';
};

const Budget = () => {
  const [budget, setBudget] = useState(INITIAL_BUDGET);
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ category: '', type: 'Income', budgeted: '', actual: '' });
  const [period, setPeriod] = useState('2026-Q1');

  const totalBudgetedIncome = budget.filter(b => b.type === 'Income').reduce((s, b) => s + b.budgeted, 0);
  const totalActualIncome = budget.filter(b => b.type === 'Income').reduce((s, b) => s + b.actual, 0);
  const totalBudgetedExpense = budget.filter(b => b.type === 'Expense').reduce((s, b) => s + b.budgeted, 0);
  const totalActualExpense = budget.filter(b => b.type === 'Expense').reduce((s, b) => s + b.actual, 0);
  const netBudget = totalBudgetedIncome - totalBudgetedExpense;
  const netActual = totalActualIncome - totalActualExpense;

  const openAdd = () => {
    setEditRow(null);
    setForm({ category: '', type: 'Income', budgeted: '', actual: '' });
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditRow(row.id);
    setForm({ category: row.category, type: row.type, budgeted: row.budgeted, actual: row.actual });
    setShowModal(true);
  };

  const handleSave = () => {
    if (!form.category) return;
    if (editRow) {
      setBudget(prev => prev.map(b => b.id === editRow ? { ...b, ...form, budgeted: parseFloat(form.budgeted) || 0, actual: parseFloat(form.actual) || 0 } : b));
    } else {
      setBudget(prev => [...prev, { id: Date.now(), ...form, budgeted: parseFloat(form.budgeted) || 0, actual: parseFloat(form.actual) || 0 }]);
    }
    setShowModal(false);
  };

  const handleDelete = (id) => setBudget(prev => prev.filter(b => b.id !== id));

  return (
    <div className="accounting-page">
      <div className="table-controls">
        <div className="table-controls-left">
        </div>
        <div className="table-controls-right">
          <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <select
              className="form-select"
              value={period}
              onChange={e => setPeriod(e.target.value)}
              style={{ minWidth: '130px', height: '38px', padding: '0.4rem 0.6rem', fontSize: '0.875rem', border: '1px solid #e2e8f0', borderRadius: '8px' }}
            >
              {['2026-Q1', '2026-Q2', '2026-Q3', '2026-Q4', '2025-Annual'].map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <button className="export-btn" style={{ background: '#fff', border: '1px solid #e2e8f0', padding: '0.45rem 1rem', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 600, color: '#334155', cursor: 'pointer' }}>↓ Export</button>
          <button className="control-btn-primary" onClick={openAdd}>
            <span className="plus-icon">+</span> Add Budget Line
          </button>
        </div>
      </div>

      <div className="acc-stats-row">
        {[
          { label: 'Budgeted Income', value: `$${totalBudgetedIncome.toLocaleString()}`, color: 'green' },
          { label: 'Actual Income', value: `$${totalActualIncome.toLocaleString()}`, color: 'blue' },
          { label: 'Budgeted Expense', value: `$${totalBudgetedExpense.toLocaleString()}`, color: 'orange' },
          { label: 'Actual Expense', value: `$${totalActualExpense.toLocaleString()}`, color: 'red' },
          { label: 'Net Budget', value: `$${netBudget.toLocaleString()}`, color: 'purple' },
          { label: 'Net Actual', value: `$${netActual.toLocaleString()}`, color: netActual >= 0 ? 'green' : 'red' },
        ].map(s => (
          <div key={s.label} className={`acc-stat-card ${s.color}`}>
            <div className="acc-stat-label">{s.label}</div>
            <div className="acc-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="table-content-card">
        <h3 className="acc-card-title">Budget vs Actual — {period}</h3>
        <div className="acc-table-wrapper products-table-container">
          <table className="acc-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Type</th>
                <th className="text-right">Budgeted</th>
                <th className="text-right">Actual</th>
                <th className="text-right">Variance</th>
                <th style={{ width: '160px' }}>Progress</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {budget.map(b => {
                const pct = b.budgeted > 0 ? Math.min((b.actual / b.budgeted) * 100, 100) : 0;
                const variance = b.type === 'Income'
                  ? b.actual - b.budgeted
                  : b.budgeted - b.actual;
                const color = getProgressColor(pct);
                return (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 500 }}>{b.category}</td>
                    <td>
                      <span className={`acc-badge ${b.type === 'Income' ? 'acc-badge-green' : 'acc-badge-red'}`}>{b.type}</span>
                    </td>
                    <td className="text-right">${b.budgeted.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="text-right">${b.actual.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="text-right" style={{ fontWeight: 600, color: variance >= 0 ? '#10b981' : '#ef4444' }}>
                      {variance >= 0 ? '+' : ''}${variance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className="budget-progress-bar" style={{ flex: 1 }}>
                          <div className={`budget-progress-fill ${color}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--etpos-text-muted,#64748b)', minWidth: '36px' }}>{pct.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="text-center">
                      <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                        <button onClick={() => openEdit(b)} style={{ background: 'none', border: '1px solid var(--etpos-border,#e2e8f0)', borderRadius: '0.35rem', padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem' }}>Edit</button>
                        <button onClick={() => handleDelete(b.id)} style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: '0.35rem', padding: '0.25rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', color: '#ef4444' }}>Del</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-container filter-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editRow ? 'Edit Budget Line' : 'Add Budget Line'}</h3>
              <button className="modal-close-btn" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-content">
              <div className="acc-form-grid">
                <div className="acc-form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Category</label>
                  <input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Marketing" />
                </div>
                <div className="acc-form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                    <option>Income</option>
                    <option>Expense</option>
                  </select>
                </div>
                <div className="acc-form-group">
                  <label>Budgeted Amount</label>
                  <input type="number" value={form.budgeted} onChange={e => setForm(p => ({ ...p, budgeted: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="acc-form-group">
                  <label>Actual Amount</label>
                  <input type="number" value={form.actual} onChange={e => setForm(p => ({ ...p, actual: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="modal-btn primary-btn" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;
